import React, { useState } from "react";
import "../../index.css";
import "../../App.css";
import {
  Row,
  Col,
  Input,
  Typography,
  Radio,
  Tag,
  Button,
  Checkbox,
  Spin,
} from "antd";
import {
  handleUpdatePainFactorWorse,
  handleUpdatePainFactorActivity,
  handleGoToNextStep,
  handleUpdateErrorsOfOrderCreamForm,
  handleGoBack,
  saveStep2Api,
} from "../../redux-toolkit/slices/orderCreamSlice";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { useSelector } from "react-redux";
import { validatePainFactors } from "../../schemas/OrderCream";
import ErrorList from "../Items/ErrorsList";
import { useSearchParams } from "react-router-dom";

const painDescriptionOptions: string[] = [
  "Movement",
  "Pressure on the area",
  "Cold weather",
  "Heat",
  "None of these",
];

const OcPainfactors = () => {
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);
  const {
    painFactors,
    isError,
    errors,
    transactionId,
    currentStep,
    isLoading,
  } = orderCreamForm;
  const { physicalActivity, painWorseDescriptions } = painFactors;
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const handlePaindurationChange = (e: any) => {
    dispatch(handleUpdatePainFactorActivity(e.target.value));
  };

  const handlePainDescriptionChange = (description: string) => {
    dispatch(handleUpdatePainFactorWorse(description));
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", prevStep.toString());
      setSearchParams(newParams, { replace: true });
      // Don't call handleGoBack() - let the parent component sync from URL
    }
  };

  const handleSubmit = async () => {
    // Validate radio button first - must be one of the valid values
    const validValues = ["sedentary", "light", "moderate", "high"];
    if (!physicalActivity || 
        physicalActivity === "" || 
        physicalActivity === null || 
        physicalActivity === undefined ||
        (typeof physicalActivity === 'string' && physicalActivity.trim() === "") ||
        !validValues.includes(physicalActivity)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose a physical activity."],
        })
      );
    }

    // Remove _id before validation
    const { _id, ...painFactorsWithoutId } = painFactors as any;
    const { isError, errors } = validatePainFactors(painFactorsWithoutId);

    if (isError) {
      return dispatch(handleUpdateErrorsOfOrderCreamForm({ isError, errors }));
    }

    if (!transactionId) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Transaction ID is missing. Please refresh the page."],
        })
      );
    }

    try {
      // Sanitize painFactors to remove _id fields
      const sanitizedPainFactors = {
        physicalActivity: painFactors.physicalActivity,
        painWorseDescriptions: painFactors.painWorseDescriptions || [],
      };

      await dispatch(
        saveStep2Api({
          transactionId,
          painFactors: sanitizedPainFactors,
        })
      ).unwrap();

      // Update URL with new step
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", "3");
      setSearchParams(newParams, { replace: true });

      dispatch(handleGoToNextStep());
    } catch (error) {
      dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Failed to save step. Please try again."],
        })
      );
    }
  };

  return (
    <div className="pain-factors-entry">
      <div className="flex flex-col gap-5 w-full">
        {/* Physical Activity Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>What is your level of physical activity? *</strong>
          </label>
          <Radio.Group
            onChange={handlePaindurationChange}
            value={physicalActivity}
          >
            <Radio value="sedentary">Sedentary</Radio>
            <Radio value="light">Light</Radio>
            <Radio value="moderate">Moderate</Radio>
            <Radio value="high">High</Radio>
          </Radio.Group>
        </div>

        {/* Pain Worsening Factors Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Does your pain get worse with any of these? (Select all that
              apply) *
            </strong>
          </label>
          <div className="flex flex-col gap-2">
            {painDescriptionOptions.map((option) => (
              <label
                key={option}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="twinge-checkbox"
                  checked={painWorseDescriptions.includes(option)}
                  onChange={() => handlePainDescriptionChange(option)}
                />
                <span className="body1">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {isError === true && (
          <div className="mt-6">
            <ErrorList errors={errors} />
          </div>
        )}

        {/* Submit Button */}
        <div
          className="flex justify-center gap-4"
          style={{ marginTop: "40px" }}
        >
          <button
            onClick={handleBack}
            className="caption2 transition-colors duration-200"
            style={{
              color: "var(--primary-500)",
              textDecoration: "none",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isLoading}
            style={{ position: "relative", minWidth: "120px" }}
          >
            {isLoading ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Spin size="small" />
                Saving...
              </span>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcPainfactors;
